import { z } from 'zod';

import { LocalDateSchema } from './datetime';

/**
 * 009 · tipos compartidos de la integración genérica (contracts/admin-api.md · "Tipos compartidos").
 *
 * Los usan `admin/sites.ts` y `admin/workers.ts` (`managed_by`) y `admin/integration.ts` (referencias
 * a entidades de Fermin). Ningún nombre de proveedor: el sistema externo se identifica solo por el
 * `system_name` que escribe el administrador.
 */

// ---------------------------------------------------------------------------
// Campos gestionados (research R-14)
// ---------------------------------------------------------------------------

/**
 * Campos de una sede que puede gestionar el sistema externo (nombres del contrato de Fermin).
 * `name`, `address` y `status` siempre; `timezone`, `latitude` y `longitude` solo si llegan.
 * `status` cubre desactivar y activar.
 */
export const SITE_MANAGED_FIELDS = [
  'name',
  'address',
  'status',
  'timezone',
  'latitude',
  'longitude',
] as const;
export const SiteManagedFieldSchema = z.enum(SITE_MANAGED_FIELDS);
export type SiteManagedField = z.infer<typeof SiteManagedFieldSchema>;

/**
 * Campos de un trabajador que puede gestionar el sistema externo. Siempre: nombres, apellidos,
 * documento, sedes, sede principal y `status` (desactivar/reactivar); `email`, `internal_code` y
 * `phone` solo si el origen los envía (un `null` explícito también cuenta).
 */
export const WORKER_MANAGED_FIELDS = [
  'first_names',
  'last_names',
  'document_type',
  'document_number',
  'site_ids',
  'primary_site_id',
  'status',
  'email',
  'internal_code',
  'phone',
] as const;
export const WorkerManagedFieldSchema = z.enum(WORKER_MANAGED_FIELDS);
export type WorkerManagedField = z.infer<typeof WorkerManagedFieldSchema>;

/**
 * `Site.managed_by` / `Worker.managed_by`: la entidad está vinculada a un registro del sistema
 * externo. `null` = gestionada solo en Fermin. `fields` son los campos de solo lectura en el Admin
 * (`SiteManagedField` o `WorkerManagedField`). `scheduled_termination_date` solo en trabajadores
 * (cese futuro informado por el origen, research R-13).
 */
export const ManagedBySchema = z.object({
  system_name: z.string().min(1).max(80),
  fields: z.array(z.string().min(1)),
  scheduled_termination_date: LocalDateSchema.nullable().optional(),
});
export type ManagedBy = z.infer<typeof ManagedBySchema>;

// ---------------------------------------------------------------------------
// Referencia a una entidad de Fermin
// ---------------------------------------------------------------------------

/**
 * Referencia legible a una sede o trabajador de Fermin, resuelta en vivo por la API (vista previa,
 * candidatos, detalle de ejecución, vínculos). `label` = nombre de la sede o "Apellidos, Nombres";
 * `detail` = dirección o documento enmascarado; `status` = estado de la entidad (`ACTIVE`…).
 */
export const EntityRefSchema = z.object({
  id: z.string().min(1),
  label: z.string(),
  detail: z.string().nullable(),
  status: z.string(),
});
export type EntityRef = z.infer<typeof EntityRefSchema>;
